import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import ContactSnapshotCard from '../../components/internal-shipments/ContactSnapshotCard';
import { downloadShipmentPdf } from '../../hooks/useInternalShipments';
import {
  useAdminInternalShipment,
  useAdminInternalShipmentMutations,
} from '../../hooks/admin/useAdminInternalShipments';
import {
  INK,
  FONTS,
  primaryBtnSx,
  ghostBtnSx,
  ledgerInputSx,
  fieldLabelSx,
} from '../../styles/ledger';
import {
  LedgerPage,
  LedgerCard,
  SectionHeader,
  StatusDot,
  KPIStrip,
  KPI,
} from '../../components/admin/LedgerPrimitives';

const statusTone = (s: string): 'active' | 'inactive' | 'warning' | 'info' | 'neutral' => {
  if (s === 'delivered') return 'active';
  if (s === 'cancelled' || s === 'rejected') return 'inactive';
  if (s === 'pending_review') return 'warning';
  if (s === 'approved' || s === 'in_transit') return 'info';
  return 'neutral';
};

const AdminInternalShipmentDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { shipment, isLoading, error } = useAdminInternalShipment(id);
  const { updateStatus, updateTracking } = useAdminInternalShipmentMutations();

  const [status, setStatus] = useState<'approved' | 'rejected' | 'in_transit' | 'delivered'>('approved');
  const [adminNotes, setAdminNotes] = useState('');
  const [carrier, setCarrier] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');

  React.useEffect(() => {
    if (shipment) {
      setAdminNotes(shipment.adminNotes || '');
      setCarrier(shipment.carrier || '');
      setTrackingNumber(shipment.trackingNumber || '');
      const next =
        shipment.status === 'pending_review' ? 'approved'
          : shipment.status === 'approved' ? 'in_transit'
          : 'delivered';
      setStatus(next);
    }
  }, [shipment]);

  const allowedStatuses = useMemo(() => {
    if (!shipment) return [] as const;
    if (shipment.status === 'pending_review') return ['approved', 'rejected'] as const;
    if (shipment.status === 'approved') return ['in_transit'] as const;
    if (shipment.status === 'in_transit') return ['delivered'] as const;
    return [] as const;
  }, [shipment]);

  if (isLoading) {
    return (
      <LedgerPage>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} sx={{ color: INK.accent }} />
        </Box>
      </LedgerPage>
    );
  }

  if (error || !shipment) {
    return (
      <LedgerPage>
        <Alert severity="error" sx={{ borderRadius: 0, fontFamily: FONTS.sans, border: `1px solid ${INK.inactive}` }}>
          Failed to load internal shipment.
        </Alert>
      </LedgerPage>
    );
  }

  const handleStatusSave = async () => {
    await updateStatus.mutateAsync({ id: shipment.id, status, adminNotes });
  };
  const handleTrackingSave = async () => {
    await updateTracking.mutateAsync({ id: shipment.id, carrier, trackingNumber, adminNotes });
  };

  return (
    <LedgerPage eyebrowFooter={`Almaship · Internal shipment · ${shipment.shipmentNumber}`}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
        <Button
          onClick={() => navigate('/admin/internal-shipments')}
          startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
          sx={{ ...ghostBtnSx, border: 'none', color: INK.muted, px: 1, '&:hover': { bgcolor: 'transparent', color: INK.accent, borderColor: 'transparent' } }}
        >
          Back to shipments
        </Button>
        <Button onClick={() => downloadShipmentPdf(shipment, true)} startIcon={<DownloadIcon sx={{ fontSize: 16 }} />} sx={primaryBtnSx}>
          Download PDF
        </Button>
      </Stack>

      <Box sx={{ mb: 5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <Box sx={{ width: 24, height: 1, bgcolor: INK.accent }} />
          <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, letterSpacing: '0.18em', color: INK.accent, fontWeight: 500, textTransform: 'uppercase' }}>
            Logistics · Shipment
          </Typography>
        </Stack>
        <Typography sx={{ fontFamily: FONTS.serif, fontSize: { xs: 44, md: 64 }, lineHeight: 0.95, letterSpacing: '-0.02em', color: INK.text, fontWeight: 400 }}>
          {shipment.shipmentNumber}
        </Typography>
        <Typography sx={{ fontFamily: FONTS.mono, fontSize: 12, color: INK.muted, mt: 1.5, letterSpacing: '0.04em' }}>
          {shipment.user?.fullName || 'Unknown user'} · {shipment.createdAt ? format(new Date(shipment.createdAt), 'yyyy-MM-dd HH:mm') : 'N/A'}
        </Typography>
        <Box sx={{ mt: 2 }}>
          <StatusDot label={shipment.statusLabel} tone={statusTone(shipment.status)} />
        </Box>
      </Box>

      <KPIStrip cols={4}>
        <KPI label="Item" value={<Typography sx={{ fontFamily: FONTS.serif, fontSize: { xs: 20, md: 24 }, letterSpacing: '-0.01em' }}>{shipment.itemDescription}</Typography>} sub="description" />
        <KPI label="Weight" value={`${shipment.weightKg} kg`} sub="declared" />
        <KPI label="Cost" value={`${shipment.currency} ${shipment.quotedCost}`} sub="quoted" accent />
        <KPI label="Tracking" value={shipment.trackingNumber || '—'} sub={shipment.trackingNumber ? 'assigned' : 'pending'} />
      </KPIStrip>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
        <ContactSnapshotCard title="Sender" contact={shipment.sender} />
        <ContactSnapshotCard title="Recipient" contact={shipment.recipient} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <LedgerCard>
          <SectionHeader number="01" eyebrow="Status" title="Status update" />
          {allowedStatuses.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 0, fontFamily: FONTS.sans, border: `1px solid ${INK.hairline}` }}>
              This shipment is in a terminal state.
            </Alert>
          ) : (
            <Stack spacing={2}>
              <Box>
                <Typography sx={fieldLabelSx}>Next status</Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as typeof status)}
                  sx={ledgerInputSx}
                >
                  {allowedStatuses.map((o) => (
                    <MenuItem key={o} value={o} sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>
                      {o.replace(/_/g, ' ')}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box>
                <Typography sx={fieldLabelSx}>Admin notes</Typography>
                <TextField
                  multiline
                  minRows={4}
                  fullWidth
                  size="small"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  sx={ledgerInputSx}
                />
              </Box>
              <Button onClick={handleStatusSave} disabled={updateStatus.isPending} sx={primaryBtnSx} startIcon={<SaveIcon sx={{ fontSize: 16 }} />}>
                Save status
              </Button>
            </Stack>
          )}
        </LedgerCard>

        <LedgerCard>
          <SectionHeader number="02" eyebrow="Carrier" title="Tracking details" />
          <Stack spacing={2}>
            <Box>
              <Typography sx={fieldLabelSx}>Carrier</Typography>
              <TextField fullWidth size="small" value={carrier} onChange={(e) => setCarrier(e.target.value)} sx={ledgerInputSx} />
            </Box>
            <Box>
              <Typography sx={fieldLabelSx}>Tracking number</Typography>
              <TextField fullWidth size="small" value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} sx={ledgerInputSx} />
            </Box>
            <Button
              onClick={handleTrackingSave}
              disabled={updateTracking.isPending || !carrier || !trackingNumber}
              sx={primaryBtnSx}
              startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
            >
              Save tracking
            </Button>
          </Stack>
        </LedgerCard>
      </Box>
    </LedgerPage>
  );
};

export default AdminInternalShipmentDetailPage;
